import { useMemo } from 'react';
import { NormalizedTask } from '../../domain/normalize';
import { computeKpis, computeRetrabajo, computeAjustesCliente, serieMensual } from '../../domain/metrics';
import { UserDoc } from '../../domain/types';
import { nameByEmail, fmtDia } from '../../domain/util';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { Card, Kpi, BarCard, DonutCard, ExportButton, BoardIntro, tooltipStyle, axisTick, GRID, CHART_CURSOR } from './shared';

const RETRABAJO_COLOR = '#db2777';
const AJUSTE_COLOR = '#f59e0b';

function SectionHead({ color, titulo, bajada }: { color: string; titulo: string; bajada: string }) {
  return (
    <div className="section-head" style={{ borderLeftColor: color }}>
      <h2>{titulo}</h2>
      <p>{bajada}</p>
    </div>
  );
}

export default function BoardRetrabajo({ tasks, users }: { tasks: NormalizedTask[]; users: UserDoc[] }) {
  const k = useMemo(() => computeKpis(tasks), [tasks]);
  const rt = useMemo(() => computeRetrabajo(tasks), [tasks]);
  const aj = useMemo(() => computeAjustesCliente(tasks), [tasks]);
  const serie = useMemo(() => serieMensual(tasks), [tasks]);

  // Resolución del campo `ajuste` (id de la tarea original) a su descripción.
  const byId = useMemo(() => {
    const m = new Map<string, NormalizedTask>();
    for (const t of tasks) m.set(String(t.id), t);
    return m;
  }, [tasks]);
  const refDe = (t: NormalizedTask) => {
    const ref = String(t.raw.ajuste || '').trim();
    const orig = byId.get(ref);
    return orig ? orig.descripcion.slice(0, 40) : ref;
  };
  // Tareas anteriores al campo `titulo` no lo tienen: caemos a la descripción,
  // pero truncada, para no convertir un párrafo entero en "título".
  const tituloDe = (t: NormalizedTask) => t.titulo || t.descripcion.slice(0, 46);

  const retrabajoCsv = rt.topTareas.map((t) => ({
    descripcion: t.descripcion, proyecto: t.proyecto, solicitante: t.solicitante, area: t.area_produccion,
    responsable: nameByEmail(t.responsable, users), reaperturas: t.retrabajos_count, estado: t.estado,
    deadline: t.deadline, entrega: t.fecha_entrega, horas: t.horas_totales,
  }));
  const ajusteCsv = aj.topTareas.map((t) => ({
    titulo: tituloDe(t), proyecto: t.proyecto, ajuste_de: refDe(t), solicitante: t.solicitante,
    area: t.area_produccion, responsable: nameByEmail(t.responsable, users), estado: t.estado,
    deadline: t.deadline, horas: t.horas_totales,
  }));

  return (
    <div className="grid">
      <BoardIntro
        titulo="Retrabajo y ajustes"
        objetivo="Dos cosas distintas: el retrabajo interno (tareas que un admin reabre pasándolas de finalizada a activa) y los ajustes pedidos por el cliente (tareas vinculadas a una anterior)."
        preguntas={['¿Cuánto trabajo se reabre y rehace internamente?', '¿Qué pedidos vuelven más veces?', '¿Cuánto del volumen son ajustes de cliente?']}
      />

      <div className="panel" style={{ borderLeft: '3px solid #f59e0b' }}>
        <strong>Cómo leer esta vista:</strong> el <strong>retrabajo</strong> es reproceso a pedido
        interno: cuenta cuántas veces un admin reabrió una tarea ya finalizada. El <strong>ajuste</strong>
        {' '}es un pedido del cliente y se registra cuando una tarea es ajuste de otra anterior (campo ajuste).
        Abajo se analizan por separado.
      </div>

      <div className="cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px,1fr))' }}>
        <Kpi label="Tareas (total)" value={String(k.qtyTareas)} />
        <Kpi label="Con retrabajo" value={String(k.qtyConRetrabajo)} sub={`${k.pctConRetrabajo}% del total`} tone={k.pctConRetrabajo > 30 ? 'warn' : undefined} help="Tareas que se reabrieron al menos una vez (un admin las pasó de finalizada a activa)." />
        <Kpi label="Reaperturas totales" value={String(k.retrabajosTotales)} help="Suma de todas las veces que las tareas se reabrieron. Una tarea reabierta 3 veces aporta 3." />
        <Kpi label="Reaperturas prom./tarea" value={String(k.retrabajoProm)} help="Promedio de reaperturas por tarea del período." />
        <Kpi label="Ajustes de cliente" value={String(k.qtyAjustes)} sub={`${k.pctAjustes}% del total`} help="Tareas que son ajuste pedido por el cliente (vinculadas a una tarea anterior por el campo ajuste)." />
      </div>

      <Card title="Evolutivo mensual: retrabajo vs ajustes" hint="Reaperturas y ajustes de cliente por mes de solicitud.">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={serie} margin={{ left: -10, right: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
            <XAxis dataKey="mes" tick={axisTick} />
            <YAxis allowDecimals={false} tick={axisTick} />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: CHART_CURSOR }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="retrabajos" name="Reaperturas" stroke={RETRABAJO_COLOR} strokeWidth={2} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="ajustes" name="Ajustes de cliente" stroke={AJUSTE_COLOR} strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* ───────── Sección Retrabajo interno ───────── */}
      <SectionHead color={RETRABAJO_COLOR} titulo="Retrabajo interno" bajada="Tareas reabiertas por un admin (finalizada → activa). Las barras suman reaperturas." />

      <div className="cards">
        <DonutCard title="Con vs sin retrabajo" hint="Tareas reabiertas al menos una vez vs las que no." data={rt.distribucion} colors={['#16a34a', RETRABAJO_COLOR]} pct />
        <BarCard title="Retrabajo por responsable" hint="Reaperturas acumuladas." data={rt.porResponsable.map((x) => ({ ...x, name: nameByEmail(x.name, users) }))} color={RETRABAJO_COLOR} horizontal />
        <BarCard title="Retrabajo por área de producción" hint="Reaperturas acumuladas." data={rt.porArea} color="#7c3aed" horizontal />
      </div>

      <Card title="Tareas con más retrabajo" hint="Ordenadas por cantidad de reaperturas." action={<ExportButton filename="retrabajo.csv" rows={retrabajoCsv} />}>
        <div style={{ overflowX: 'auto', maxHeight: 380 }}>
          <table>
            <thead><tr><th>Descripción</th><th>Proyecto</th><th>Solicitante</th><th>Área</th><th>Responsable</th><th className="num">Reaperturas</th><th>Estado</th><th>Deadline</th><th className="num">Horas</th></tr></thead>
            <tbody>
              {rt.topTareas.map((t) => (
                <tr key={t.id}>
                  <td>{t.descripcion.slice(0, 46)}</td><td>{t.proyecto || '—'}</td><td>{t.solicitante}</td><td>{t.area_produccion || '—'}</td>
                  <td>{nameByEmail(t.responsable, users)}</td>
                  <td className="num"><span className="badge urg-alta">{t.retrabajos_count}</span></td>
                  <td>{t.estado}</td><td>{fmtDia(t.deadline)}</td><td className="num">{t.horas_totales || '—'}</td>
                </tr>
              ))}
              {rt.topTareas.length === 0 && <tr><td colSpan={9} className="muted">Sin tareas con retrabajo en la vista.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* ───────── Sección Ajuste de cliente ───────── */}
      <SectionHead color={AJUSTE_COLOR} titulo="Ajuste de cliente" bajada="Tareas que son ajuste de otra anterior (campo ajuste). Las barras cuentan tareas de ajuste." />

      <div className="cards">
        <DonutCard title="Original vs ajuste de cliente" hint="Cuántas tareas son ajuste de otra vs originales." data={aj.distribucion} colors={['#4f46e5', AJUSTE_COLOR]} pct />
        <BarCard title="Ajustes por solicitante" hint="Tareas de ajuste por espacio / cliente." data={aj.porSolicitante} color={AJUSTE_COLOR} horizontal />
        <BarCard title="Ajustes por proyecto" hint="Tareas de ajuste por proyecto." data={aj.porProyecto} color="#b45309" horizontal />
      </div>

      <Card title="Tareas de ajuste de cliente" hint="Tareas marcadas como ajuste de otra; 'Ajuste de' es la tarea original referenciada." action={<ExportButton filename="ajustes.csv" rows={ajusteCsv} />}>
        <div style={{ overflowX: 'auto', maxHeight: 380 }}>
          <table>
            <thead><tr><th>Título</th><th>Proyecto</th><th>Ajuste de</th><th>Solicitante</th><th>Área</th><th>Responsable</th><th>Estado</th><th>Deadline</th><th className="num">Horas</th></tr></thead>
            <tbody>
              {aj.topTareas.map((t) => (
                <tr key={t.id}>
                  <td>{tituloDe(t)}</td><td>{t.proyecto || '—'}</td><td>{refDe(t) || '—'}</td><td>{t.solicitante}</td><td>{t.area_produccion || '—'}</td>
                  <td>{nameByEmail(t.responsable, users)}</td>
                  <td>{t.estado}</td><td>{fmtDia(t.deadline)}</td><td className="num">{t.horas_totales || '—'}</td>
                </tr>
              ))}
              {aj.topTareas.length === 0 && <tr><td colSpan={9} className="muted">Sin tareas de ajuste de cliente en la vista.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
